/**
 * 统一 UI 常量
 *
 * 各项目通用的 UI 常量定义，避免硬编码散落在各处。
 */

/**
 * 响应式断点 (px)
 *
 * 唯一的断点定义来源，styles/responsive.ts 从此处引用。
 * 值与 Ant Design Grid 断点保持一致。
 */
export const BREAKPOINTS = {
  /** 超小屏 (手机竖屏, <480px) */
  xs: 480,
  /** 小屏 (手机横屏, >=576px) */
  sm: 576,
  /** 中屏 (平板, >=768px) */
  md: 768,
  /** 大屏 (小桌面, >=992px) */
  lg: 992,
  /** 超大屏 (标准桌面, >=1200px) */
  xl: 1200,
  /** 超超大屏 (大桌面, >=1600px) */
  xxl: 1600,
} as const;

/**
 * 防抖延迟常量 (ms)
 */
export const DEBOUNCE_DELAY = {
  /** 搜索输入防抖，默认 300ms */
  SEARCH: 300,
  /** 窗口 resize 防抖，默认 200ms */
  RESIZE: 200,
  /** 滚动防抖，默认 100ms */
  SCROLL: 100,
  /** 表单验证防抖，默认 500ms */
  VALIDATE: 500,
  /** 输入变更检测，默认 400ms */
  INPUT_CHANGE: 400,
} as const;

/**
 * 表格默认配置
 */
export const TABLE_DEFAULTS = {
  /** 默认每页条数 */
  PAGE_SIZE: 10,
  /** 可选的每页条数 */
  PAGE_SIZE_OPTIONS: [10, 20, 50, 100],
  /** 表格滚动宽度（设为 'max-content' 时自动） */
  SCROLL_X: 'max-content' as const,
  /** 行唯一标识字段 */
  ROW_KEY: 'id' as const,
  /** 是否显示每页条数切换 */
  SHOW_SIZE_CHANGER: true,
  /** 是否显示总数 */
  SHOW_TOTAL: true,
} as const;

/**
 * 动画时长常量 (ms)
 */
export const ANIMATION_DURATION = {
  /** 快速动画 (hover/focus) */
  FAST: 150,
  /** 标准动画 (展开/折叠) */
  NORMAL: 300,
  /** 慢速动画 (页面切换) */
  SLOW: 500,
  /** 骨架屏动画周期 */
  SKELETON: 1500,
} as const;

/**
 * 图标尺寸常量 (px)
 */
export const ICON_SIZE = {
  /** 小图标 */
  SMALL: 14,
  /** 标准图标 */
  MEDIUM: 16,
  /** 大图标 */
  LARGE: 20,
  /** 超大图标 */
  XLARGE: 24,
} as const;

/**
 * 间距常量 (px)
 */
export const SPACING = {
  /** 超小间距 */
  XS: 4,
  /** 小间距 */
  SM: 8,
  /** 标准间距 */
  MD: 16,
  /** 大间距 */
  LG: 24,
  /** 超大间距 */
  XL: 32,
  /** 页面间距 */
  PAGE: 24,
} as const;

/**
 * Z-Index 层级
 */
export const Z_INDEX = {
  /** 下拉菜单 */
  DROPDOWN: 1050,
  /** 固定元素 (header/sidebar) */
  FIXED: 100,
  /** 弹窗遮罩 */
  MODAL_MASK: 1000,
  /** 弹窗 */
  MODAL: 1001,
  /** 全局消息 */
  MESSAGE: 1010,
  /** 通知 */
  NOTIFICATION: 1010,
  /** Tooltip */
  TOOLTIP: 1070,
} as const;

/**
 * 错误展示类型
 *
 * 定义 API 错误的展示方式，用于 request 和 utils 模块。
 * 放在 constants 层避免 utils ↔ request 循环依赖。
 */
export enum ErrorShowType {
  /** 不显示 */
  SILENT = 0,
  /** 警告消息 */
  WARN_MESSAGE = 1,
  /** 错误消息 */
  ERROR_MESSAGE = 2,
  /** 通知框 */
  NOTIFICATION = 3,
  /** 跳转页面 */
  REDIRECT = 9,
}
